import { Chip, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremiumProps,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useMemo, useState } from "react";
import { CommentedEvent } from "src/store/api/generatedApi";
import { CommentDetailPanel } from "./CommentDetailPanel";
import { DispositionChip, dispositionFor } from "./DispositionChip";
import { UserLabel } from "components/common/userLabel/UserLabel";
import { userDisplayName } from "src/utils/userDisplay";

dayjs.extend(utc);

const GET_DETAIL_PANEL_HEIGHT = () => "auto" as const;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const EVENT_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const CommentReviewTable = ({
  commentedEvents,
  location,
}: {
  commentedEvents: CommentedEvent[];
  location: string;
}) => {
  const { t } = useTenantTranslation();
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(
    () => new Set()
  );

  const eventTypeOptions = useMemo(
    () => [...new Set(commentedEvents.map((row) => row.event.data.event_type))],
    [commentedEvents]
  );

  const columns = useMemo<GridColDef<CommentedEvent>[]>(
    () => [
      {
        field: "comment",
        headerName: t("comment"),
        flex: 1,
        minWidth: 280,
        renderCell: (params) => (
          <Tooltip title={params.row.comment}>
            <Typography variant="body2" noWrap>
              {params.row.comment}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "commenter",
        headerName: t("commenter"),
        width: 200,
        renderHeader: () => (
          <Tooltip title={t("commentReviewCommenterTooltip")}>
            <Typography variant="body2" fontWeight={500}>
              {t("commenter")}
            </Typography>
          </Tooltip>
        ),
        valueGetter: (_value, row) => userDisplayName(row.commenter),
        renderCell: (params) => <UserLabel user={params.row.commenter} />,
      },
      {
        field: "time",
        headerName: t("time"),
        width: 180,
        valueGetter: (_value, row) =>
          `${dayjs.utc(row.event.data.start).format(EVENT_TIME_FORMAT)} UTC`,
      },
      {
        field: "eventType",
        headerName: t("type"),
        width: 110,
        type: "singleSelect",
        valueOptions: eventTypeOptions,
        valueGetter: (_value, row) => row.event.data.event_type,
        renderCell: (params) => (
          <Chip size="small" label={params.row.event.data.event_type} />
        ),
      },
      {
        field: "disposition",
        headerName: t("disposition"),
        width: 140,
        sortable: false,
        type: "singleSelect",
        valueOptions: [
          { value: "NOT_REVIEWED", label: t("notReviewed") },
          { value: "REVIEWED_NO_CHANGE", label: t("reviewed") },
          { value: "EDITED", label: t("edited") },
        ],
        valueGetter: (_value, row) => dispositionFor(row),
        renderCell: (params) => {
          const disposition = dispositionFor(params.row);
          const { label, actor, byLabel } = {
            EDITED: {
              label: t("edited"),
              actor: params.row.last_edited_by,
              byLabel: t("editedBy"),
            },
            REVIEWED_NO_CHANGE: {
              label: t("reviewed"),
              actor: params.row.reviewed_by,
              byLabel: t("reviewedBy"),
            },
            NOT_REVIEWED: {
              label: t("notReviewed"),
              actor: null,
              byLabel: undefined,
            },
          }[disposition];
          const actorDisplayName = actor ? userDisplayName(actor) : undefined;
          const tooltip =
            byLabel && actorDisplayName
              ? `${byLabel} ${actorDisplayName}`
              : undefined;
          return (
            <DispositionChip
              disposition={disposition}
              label={label}
              tooltip={tooltip}
            />
          );
        },
      },
    ],
    [t, eventTypeOptions]
  );

  const getRowId = useCallback((row: CommentedEvent) => row.event.id, []);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps<CommentedEvent>["getDetailPanelContent"]>
  >(
    ({ row }) => (
      <CommentDetailPanel commentedEvent={row} location={location} />
    ),
    [location]
  );

  const handleExpandedRowIdsChange = useCallback(
    (ids: Set<GridRowId>) => {
      const newlyExpanded = [...ids].find((id) => !expandedRowIds.has(id));
      setExpandedRowIds(
        newlyExpanded === undefined ? new Set() : new Set([newlyExpanded])
      );
    },
    [expandedRowIds]
  );

  return (
    <HotkeyCompliantDataGrid<CommentedEvent>
      rows={commentedEvents}
      columns={columns}
      getRowId={getRowId}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={GET_DETAIL_PANEL_HEIGHT}
      detailPanelExpandedRowIds={expandedRowIds}
      onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
      showToolbar
      disableRowSelectionOnClick
      pagination
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
    />
  );
};
